"use client";

import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IconCheck } from "@tabler/icons-react";
import { Typography } from "@/components/Typography";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type FormSelectOption = {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
};

interface FormSelectFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  title?: string;
  placeholder?: string;
  options: readonly FormSelectOption[];
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  onChange?: (value: string) => void;
  inlineExpanded?: boolean;
  onInlineExpandedChange?: (open: boolean) => void;
}

const FormSelectField = <TFieldValues extends FieldValues>({
  name,
  control,
  label,
  title,
  placeholder,
  options,
  disabled,
  className,
  contentClassName,
  onChange,
  inlineExpanded,
  onInlineExpandedChange,
}: FormSelectFieldProps<TFieldValues>) => {
  const isInline = typeof onInlineExpandedChange === "function";
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isInline ? Boolean(inlineExpanded) : internalOpen;

  const handleInlineToggle = () => {
    if (disabled) return;
    onInlineExpandedChange?.(!isOpen);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const selectedOption = options.find((opt) => opt.value === field.value);
        const isSelected = Boolean(selectedOption);

        return (
          <div className="flex flex-col gap-1.5">
            {label && <Typography variant="button-sm">{label}</Typography>}

            {isInline ? (
              <div className="space-y-2">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={handleInlineToggle}
                  aria-expanded={isOpen}
                  className={cn(
                    "flex h-12 w-full items-center justify-between gap-2 rounded-full border bg-white px-4 text-base text-left shadow-none transition-colors hover:border-primary",
                    isOpen && "border-primary",
                    fieldState.error &&
                      "border-error-foreground ring-1 ring-error-foreground",
                    disabled && "cursor-not-allowed opacity-50",
                    className,
                  )}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {selectedOption?.icon ? (
                      <selectedOption.icon
                        className={cn(
                          "h-5 w-5 shrink-0",
                          isSelected ? "text-body" : "text-caption",
                        )}
                      />
                    ) : null}
                    <span
                      className={cn(
                        "truncate",
                        isSelected
                          ? "text-body font-semibold"
                          : "text-caption font-normal",
                      )}
                    >
                      {selectedOption ? selectedOption.label : placeholder}
                    </span>
                  </div>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 transition-transform duration-200",
                      isSelected ? "text-body" : "text-caption",
                      isOpen && "rotate-180",
                    )}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: "auto" }}
                      exit={{ height: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                      className="rounded-2xl border bg-white p-1 space-y-1 max-h-[300px] overflow-y-auto"
                    >
                      {title ? (
                        <div className="px-3 pt-2 pb-1">
                          <Typography variant="button-md">{title}</Typography>
                        </div>
                      ) : null}
                      {options.map((option) => {
                        const isOptionSelected = option.value === field.value;

                        return (
                          <button
                            key={option.value}
                            type="button"
                            disabled={disabled || option.disabled}
                            onClick={() => {
                              if (option.disabled) return;
                              field.onChange(option.value);
                              onChange?.(option.value);
                              onInlineExpandedChange?.(false);
                            }}
                            className={cn(
                              "flex h-11 w-full items-center justify-between rounded-xl px-3 text-left transition-colors hover:bg-subtle",
                              isOptionSelected &&
                                "bg-subtle text-body font-semibold",
                              option.disabled &&
                                "pointer-events-none opacity-40 cursor-not-allowed hover:bg-transparent",
                            )}
                          >
                            <div className="flex items-center gap-2.5">
                              {option.icon ? (
                                <option.icon className="h-5 w-5 shrink-0 text-body" />
                              ) : null}
                              <span>{option.label}</span>
                            </div>
                            {isOptionSelected && (
                              <IconCheck
                                stroke={2}
                                className="mr-3 h-5 w-5 shrink-0"
                              />
                            )}
                          </button>
                        );
                      })}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            ) : (
              <Select
                value={field.value ?? ""}
                onValueChange={(val) => {
                  field.onChange(val);
                  onChange?.(val);
                }}
                disabled={disabled}
                open={internalOpen}
                onOpenChange={setInternalOpen}
              >
                <SelectTrigger
                  aria-invalid={!!fieldState.error}
                  className={cn(
                    "h-12 w-full gap-1.5 rounded-full border bg-white px-4 text-base shadow-none hover:border-primary data-[state=open]:border-primary [&>svg]:shrink-0",
                    isSelected
                      ? "text-body font-semibold"
                      : "text-caption font-normal data-[placeholder]:text-caption",
                    fieldState.error &&
                      "border-error-foreground ring-1 ring-error-foreground",
                    className,
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left">
                    {selectedOption?.icon ? (
                      <selectedOption.icon
                        className={cn(
                          "h-5 w-5 shrink-0",
                          isSelected ? "text-body" : "text-caption",
                        )}
                      />
                    ) : null}
                    {/* Own text node instead of <SelectValue>: Radix ignores className/style on it, so it can't truncate */}
                    <span className="min-w-0 flex-1 truncate">
                      {selectedOption ? selectedOption.label : placeholder}
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent
                  sideOffset={4}
                  className={cn(
                    "max-h-[300px] overflow-y-auto rounded-2xl border bg-white p-1 shadow-2xl",
                    // Small screen: full available width; iPad / desktop: wider comfortable min-width
                    "w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[max(var(--radix-select-trigger-width),20rem)] max-w-[calc(100vw-2rem)]",
                    contentClassName,
                  )}
                >
                  {title ? (
                    <div className="px-3 pt-2 pb-1">
                      <Typography variant="button-md">{title}</Typography>
                    </div>
                  ) : null}
                  {options.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      className={cn(
                        "h-11 rounded-xl px-3 cursor-pointer max-lg:whitespace-nowrap hover:bg-subtle focus:bg-subtle data-[highlighted]:bg-subtle data-[state=checked]:bg-subtle",
                        option.disabled &&
                          "pointer-events-none opacity-40 cursor-not-allowed hover:bg-transparent data-[highlighted]:bg-transparent",
                      )}
                    >
                      <div className="flex items-center gap-2.5">
                        {option.icon ? (
                          <option.icon className="h-5 w-5 shrink-0 text-body" />
                        ) : null}
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {fieldState.error && (
              <Typography variant="body2" className="text-error-foreground">
                {fieldState.error.message}
              </Typography>
            )}
          </div>
        );
      }}
    />
  );
};

export default FormSelectField;
